(function () {
  'use strict';

  function createController(marquee) {
    if (!marquee || marquee.__wcsCompanyPerksController) {
      return marquee ? marquee.__wcsCompanyPerksController : null;
    }

    const track = marquee.querySelector('.company-perks-marquee__track');
    const firstGroup = marquee.querySelector('.company-perks-marquee__group');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const resumeDelay = (Number(marquee.dataset.resumeDelay) || 3) * 1000;
    let animation = null;
    let resumeTimeout = null;
    let pointerState = 'idle';
    let pointerId = null;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let pointerLastX = 0;
    let pointerHovered = false;
    let focusWithin = false;

    function getAnimation() {
      if (!track || typeof track.getAnimations !== 'function') {
        return null;
      }

      return track.getAnimations()[0] || null;
    }

    function clearResumeTimeout() {
      if (resumeTimeout) {
        window.clearTimeout(resumeTimeout);
        resumeTimeout = null;
      }
    }

    function pauseAnimation() {
      clearResumeTimeout();
      animation = getAnimation();

      if (animation) {
        animation.pause();
      }
    }

    function scheduleResume() {
      clearResumeTimeout();

      if (reducedMotion.matches || pointerHovered || focusWithin || pointerState === 'dragging') {
        return;
      }

      animation = getAnimation() || animation;

      if (!animation) {
        return;
      }

      resumeTimeout = window.setTimeout(function () {
        if (!pointerHovered && !focusWithin && pointerState !== 'dragging') {
          animation.play();
        }
        resumeTimeout = null;
      }, resumeDelay);
    }

    function animationDuration() {
      if (!animation || !animation.effect) {
        return 0;
      }

      const timing = animation.effect.getTiming();
      return typeof timing.duration === 'number' ? timing.duration : 0;
    }

    function normalizeTime(time, duration) {
      return ((time % duration) + duration) % duration;
    }

    function scrub(deltaX) {
      const groupWidth = firstGroup ? firstGroup.getBoundingClientRect().width : 0;
      const duration = animationDuration();

      if (!groupWidth || !duration || !animation) {
        return;
      }

      const currentTime = Number(animation.currentTime) || 0;
      const directionMultiplier = marquee.classList.contains('company-perks-marquee--left-to-right') ? 1 : -1;
      animation.currentTime = normalizeTime(
        currentTime + (directionMultiplier * deltaX / groupWidth * duration),
        duration
      );
    }

    function beginPointer(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      if (event.pointerType === 'mouse' && event.cancelable) {
        event.preventDefault();
      }

      animation = getAnimation();

      if (!animation) {
        return;
      }

      pauseAnimation();
      pointerState = 'pending';
      pointerId = event.pointerId;
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
      pointerLastX = event.clientX;
    }

    function movePointer(event) {
      if (pointerState === 'idle' || event.pointerId !== pointerId || !animation) {
        return;
      }

      const totalX = event.clientX - pointerStartX;
      const totalY = event.clientY - pointerStartY;

      if (pointerState === 'pending') {
        if (Math.abs(totalX) < 6 && Math.abs(totalY) < 6) {
          return;
        }

        if (Math.abs(totalY) > Math.abs(totalX)) {
          pointerState = 'vertical';
          return;
        }

        pointerState = 'dragging';
        marquee.classList.add('is-dragging');

        if (typeof marquee.setPointerCapture === 'function') {
          marquee.setPointerCapture(event.pointerId);
        }
      }

      if (pointerState !== 'dragging') {
        return;
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      scrub(event.clientX - pointerLastX);
      pointerLastX = event.clientX;
    }

    function finishPointer(event) {
      if (pointerState === 'idle' || (event && typeof event.pointerId === 'number' && event.pointerId !== pointerId)) {
        return;
      }

      marquee.classList.remove('is-dragging');
      pointerState = 'idle';
      pointerId = null;
      scheduleResume();
    }

    function handlePointerEnter(event) {
      if (event.pointerType !== 'mouse' || !window.matchMedia('(hover: hover)').matches) {
        return;
      }

      pointerHovered = true;
      pauseAnimation();
    }

    function handlePointerLeave(event) {
      if (event.pointerType !== 'mouse') {
        return;
      }

      pointerHovered = false;
      scheduleResume();
    }

    function handleFocusIn() {
      focusWithin = true;
      pauseAnimation();
    }

    function handleFocusOut() {
      focusWithin = false;
      scheduleResume();
    }

    function handleNativeDrag(event) {
      event.preventDefault();
    }

    function destroy() {
      clearResumeTimeout();
      marquee.removeEventListener('pointerdown', beginPointer);
      marquee.removeEventListener('pointerenter', handlePointerEnter);
      marquee.removeEventListener('pointerleave', handlePointerLeave);
      marquee.removeEventListener('focusin', handleFocusIn);
      marquee.removeEventListener('focusout', handleFocusOut);
      marquee.removeEventListener('dragstart', handleNativeDrag);
      window.removeEventListener('pointermove', movePointer);
      window.removeEventListener('pointerup', finishPointer);
      window.removeEventListener('pointercancel', finishPointer);
      window.removeEventListener('blur', finishPointer);
      delete marquee.__wcsCompanyPerksController;
    }

    marquee.addEventListener('pointerdown', beginPointer);
    marquee.addEventListener('pointerenter', handlePointerEnter);
    marquee.addEventListener('pointerleave', handlePointerLeave);
    marquee.addEventListener('focusin', handleFocusIn);
    marquee.addEventListener('focusout', handleFocusOut);
    marquee.addEventListener('dragstart', handleNativeDrag);
    window.addEventListener('pointermove', movePointer, { passive: false });
    window.addEventListener('pointerup', finishPointer);
    window.addEventListener('pointercancel', finishPointer);
    window.addEventListener('blur', finishPointer);

    const controller = { destroy: destroy };
    marquee.__wcsCompanyPerksController = controller;
    return controller;
  }

  function boot(root) {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
    scope.querySelectorAll('[data-company-perks-standalone]').forEach(createController);

    if (scope.matches && scope.matches('[data-company-perks-standalone]')) {
      createController(scope);
    }
  }

  if (window.wcsStandaloneCompanyPerks) {
    window.wcsStandaloneCompanyPerks.boot(document);
    return;
  }

  window.wcsStandaloneCompanyPerks = { boot: boot };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-company-perks-standalone]').forEach(function (marquee) {
      if (marquee.__wcsCompanyPerksController) {
        marquee.__wcsCompanyPerksController.destroy();
      }
    });
  });
})();
